"use client"

import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import { ChevronRightSmIcon } from "@/components/ui/icons"
import { healthToolsKeys as k } from "@/i18n/health-tools-keys"
import { useCountry } from "@/components/layout/country-provider"
import { otherHealthTools } from "@/lib/health-tools"
import { useLocale } from "next-intl"
import { useLocalePath } from "@/lib/use-localized-navigation"

/**
 * "Other calculators" — the cross-link section every `/tools/*` page renders.
 *
 * Lists `HEALTH_TOOLS` minus the current page, filtered to what the visitor's
 * country actually has, so adding a calculator to the registry makes it appear
 * on every existing tool page at once. That is the point: these pages are
 * organic-search entry points, and a visitor who landed on one is the most
 * likely person to want another.
 *
 * Renders nothing when there is no other tool — the same shape
 * `doctors-section` and `testimonials-section` use for an empty list. Today
 * that is the normal case: the BMR and BMI calculators are on separate
 * branches, so each knows only itself. Listing a sibling whose route is absent
 * would put a 404 on a page whose whole purpose is internal linking, which is
 * worse than showing nothing.
 */
export function OtherToolsSection({
  currentToolId,
  className,
}: {
  currentToolId: string
  className?: string
}) {
  const t = useTranslations("healthTools")
  const lp = useLocalePath()
  const locale = useLocale()
  const { code: country } = useCountry()

  // Locale as well as country: a tool that exists in this market but is not
  // translated into the locale being read would be a 404 behind the card.
  const tools = otherHealthTools(currentToolId, country, locale)
  if (tools.length === 0) return null

  return (
    <section
      data-testid="other-tools-section"
      className={className ?? "pb-16 md:pb-24"}
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        <div className="mx-auto max-w-[760px]">
          <h2 className="type-display-sm text-neutral-900">{t(k.heading)}</h2>
          <p className="type-body-lg mt-4 text-neutral-600">{t(k.intro)}</p>
        </div>

        <ul className="mx-auto mt-8 grid max-w-[760px] grid-cols-1 gap-4 sm:grid-cols-2">
          {tools.map((tool) => (
            <li key={tool.id}>
              {/* Whole card is the link — a bigger target than a trailing
                  "open" affordance, and one link per card keeps the internal
                  link graph clean rather than doubling every destination. */}
              <Link
                href={lp(tool.path)}
                data-testid={`other-tool-${tool.id}`}
                className="group focus-visible:ring-aubergine-400 block h-full rounded-[16px] bg-white p-6 transition-shadow hover:shadow-[0_2px_16px_rgba(0,0,0,0.06)] focus-visible:ring-2 focus-visible:outline-none"
              >
                <span className="type-title-md flex items-center gap-1 text-neutral-900">
                  {t(tool.titleKey)}
                  <ChevronRightSmIcon
                    width={16}
                    height={16}
                    className="text-neutral-400 transition-transform duration-200 ease-out group-hover:translate-x-1"
                  />
                </span>
                <span className="type-body-md mt-2 block text-neutral-600">
                  {t(tool.descKey)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
